// Hook: useVisualViewport
//
// Reports the on-screen keyboard inset on mobile web. When a software
// keyboard opens, the browser shrinks the visual viewport while the
// layout viewport (`window.innerHeight`) stays put; the difference is
// the space the keyboard occupies. The mobile room reserves that space
// at the bottom of its column so the composer and the last message ride
// above the keyboard instead of behind it.
//
// SSR / test safe: when `window.visualViewport` is unavailable the hook
// reports a zero inset and never touches the DOM.

import { useEffect, useState } from "react";

function readKeyboardInset(): number {
  if (typeof window === "undefined") return 0;
  const viewport = window.visualViewport;
  if (!viewport) return 0;
  // `offsetTop` covers the rare case where the page is scrolled under a
  // pinned-zoom visual viewport; clamp negatives to 0. Safari keeps
  // `innerHeight` while the keyboard is up, but the iOS app's web view
  // reports a shrunken `innerHeight` too, so neither says how much the
  // keyboard covers. Where the page really ends does: the shell keeps its
  // full height under the keyboard.
  const layoutHeight = Math.max(
    window.innerHeight,
    Math.round(document.body.getBoundingClientRect().bottom),
  );
  const inset = layoutHeight - viewport.height - viewport.offsetTop;
  return inset > 0 ? inset : 0;
}

/**
 * Returns the current software-keyboard inset in CSS pixels (0 when the
 * keyboard is closed or the API is unavailable).
 */
export function useKeyboardInset(): number {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    const viewport = typeof window !== "undefined" ? window.visualViewport : null;
    if (!viewport) return;
    const update = () => {
      // WebKit pans the whole page up to reveal a focused field before the
      // layout makes room for the keyboard; that pan pushes the header under
      // the status bar, out of reach. The layout already keeps the composer
      // above the keyboard, so the page is put back where it belongs.
      if (window.scrollY !== 0) window.scrollTo(0, 0);
      setInset(readKeyboardInset());
    };
    update();
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
    };
  }, []);

  return inset;
}
